import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';

/**
 * Picker dialog shown after the user draws a new polygon on a well's map.
 * The well doesn't own polygons — its DRILLED_ON tracts do — so we have to
 * ask which tract the freshly drawn shape belongs to.
 *
 * The dropdown lists every drilledOn tract and tags each as "(no polygon)"
 * or "(replace existing)" so the user can tell at a glance which choices
 * are additive vs destructive. Default selection lands on the first tract
 * that has no geometry, since that is overwhelmingly the common case
 * (drawing in to fill a gap, not to redraw an existing one).
 */
export default function TractPolygonTargetDialog({ open, onClose, onConfirm, tracts = [] }) {
    const pickDefault = () => {
        const firstEmpty = tracts.find((t) => !t.geometry);
        return (firstEmpty || tracts[0])?.id ?? '';
    };

    const [selectedId, setSelectedId] = useState(pickDefault);

    useEffect(() => {
        if (open) setSelectedId(pickDefault());
        // We deliberately reset selection every time the dialog reopens —
        // the previous pick is rarely the right one for a new drawing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleConfirm = () => {
        if (!selectedId) return;
        onConfirm(selectedId);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Save polygon to which tract?</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ marginBottom: 16 }}>
                    Choose which of this well's DRILLED_ON tracts should receive the polygon
                    you just drew. Tracts marked <em>(no polygon)</em> don't have a saved
                    shape yet; choosing a tract that already has one will replace it.
                </DialogContentText>
                <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="tract-polygon-target-label">Tract</InputLabel>
                    <Select
                        labelId="tract-polygon-target-label"
                        label="Tract"
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value)}
                    >
                        {tracts.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.name || t.id}
                                <span style={{ marginLeft: 8, color: t.geometry ? '#c62828' : '#2e7d32', fontSize: '0.85em' }}>
                                    {t.geometry ? '(replace existing)' : '(no polygon)'}
                                </span>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    disabled={!selectedId}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
